export interface IScormInteraction {
  index: number
  id: string
  type: string
  response: string
  result: string
  correctResponse: string
}

export interface IParsedScormData {
  scoreRaw: string
  scoreMax: string
  scoreScaled: string
  lessonStatus: string
  duration: string
  interactions: IScormInteraction[]
  raw: Record<string, string>
}

// SCORM time strings: 1.2 CMITimespan "HHHH:MM:SS.ss" or 2004 ISO-8601 "PT#H#M#S".
const formatScormTime = (value: string): string => {
  if (!value) return ''

  let seconds = 0
  const cmi12 = value.match(/^(\d+):(\d{2}):(\d{2})(\.\d+)?$/)
  const iso = value.match(/^P(?:T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?)?$/)

  if (cmi12) {
    seconds = Number(cmi12[1]) * 3600 + Number(cmi12[2]) * 60 + Number(cmi12[3])
  } else if (iso) {
    seconds = Number(iso[1] ?? 0) * 3600 + Number(iso[2] ?? 0) * 60 + Number(iso[3] ?? 0)
  } else {
    return value
  }

  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const parts: string[] = []
  if (h > 0) parts.push(`${h} sa`)
  if (m > 0) parts.push(`${m} dk`)
  if (s > 0 || parts.length === 0) parts.push(`${s} sn`)
  return parts.join(' ')
}

export interface IQuizAttempt {
  attemptNo: number
  date: string
  data: IParsedScormData | null
}

export const parseQuizAttempts = (json: string | null): IQuizAttempt[] => {
  if (!json) return []
  try {
    const arr = JSON.parse(json)
    if (!Array.isArray(arr)) return []
    return arr.map((a: any, i: number) => ({
      attemptNo: a.AttemptNo ?? i + 1,
      date: a.Date ?? '',
      data: parseScormData(typeof a.Data === 'string' ? a.Data : JSON.stringify(a.Data)),
    }))
  } catch {
    return []
  }
}

export const formatSeconds = (totalSeconds: number): string => {
  if (!totalSeconds || totalSeconds <= 0) return ''
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = Math.floor(totalSeconds % 60)
  const parts: string[] = []
  if (h > 0) parts.push(`${h} sa`)
  if (m > 0) parts.push(`${m} dk`)
  if (s > 0 || parts.length === 0) parts.push(`${s} sn`)
  return parts.join(' ')
}

export const parseScormData = (json: string | null): IParsedScormData | null => {
  if (!json) return null

  let cmi: Record<string, string>
  try {
    cmi = JSON.parse(json)
  } catch {
    return null
  }

  const get = (...keys: string[]): string => {
    for (const k of keys) {
      if (cmi[k] !== undefined && cmi[k] !== '') return cmi[k]
    }
    return ''
  }

  // Collect interaction indexes present in the data
  const interactionIndexes = new Set<number>()
  Object.keys(cmi).forEach(key => {
    const m = key.match(/^cmi\.interactions\.(\d+)\./)
    if (m) interactionIndexes.add(Number(m[1]))
  })

  const interactions: IScormInteraction[] = Array.from(interactionIndexes)
    .sort((a, b) => a - b)
    .map(n => {
      const correctKeys = Object.keys(cmi)
        .filter(k => k.match(new RegExp(`^cmi\\.interactions\\.${n}\\.correct_responses\\.\\d+\\.pattern$`)))
        .map(k => cmi[k])
      return {
        index: n,
        id: get(`cmi.interactions.${n}.id`),
        type: get(`cmi.interactions.${n}.type`),
        response: get(`cmi.interactions.${n}.student_response`, `cmi.interactions.${n}.learner_response`),
        result: get(`cmi.interactions.${n}.result`),
        correctResponse: correctKeys.join(', '),
      }
    })

  return {
    scoreRaw: get('cmi.core.score.raw', 'cmi.score.raw'),
    scoreMax: get('cmi.core.score.max', 'cmi.score.max'),
    scoreScaled: get('cmi.score.scaled'),
    lessonStatus: get('cmi.core.lesson_status', 'cmi.completion_status', 'cmi.success_status'),
    // total_time is accumulated by a full LMS; packages only write session_time, so fall back to it.
    duration: formatScormTime(get('cmi.core.total_time', 'cmi.total_time', 'cmi.core.session_time', 'cmi.session_time')),
    interactions,
    raw: cmi,
  }
}
